import type {
  CmmdRuntimeInventoryRuntime,
  CmmdRuntimeInventoryThreadSummary,
  CmmdRuntimeThreadReplica,
  EnvironmentId,
  ScopedThreadRef,
} from "@cmmd-forge/contracts";
import {
  decodeCmmdRuntimeThreadGoalMetadata,
  decodeCmmdRuntimeThreadWorkspaceMetadata,
} from "@cmmd-forge/contracts";
import { useMemo } from "react";
import { useCmmdRuntimeInventorySnapshot } from "../../cmmd/cmmdRuntimeInventoryResource";
import { providerPolicyForAccess, useForgeProviderAccess } from "../../cmmd/forgeProviderAccess";
import { useComposerThreadDraft } from "../../composerDraftStore";
import { getStoredLocalDeviceRuntimeId } from "../../deviceRegistryIdentity";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import { usePrimaryEnvironmentId } from "../../environments/primary";
import { useSavedEnvironmentRuntimeStore } from "../../environments/runtime/savedEnvironmentRuntimeStore";
import { useServerConfig } from "../../rpc/serverState";
import { useSettings } from "../../hooks/useSettings";
import { resolveComposerTurnPlan } from "./composerTurnPlan";
import { useDraftProviderInventory } from "../newThread/useDraftProviderInventory";
import type { RemoteThreadComposerContext } from "./remoteThreadComposerContext";
import { resolveRemoteThreadProviderIdentity } from "./remoteThreadProviderIdentity";

/** Read the published catalog and owner policy without connecting the Environment. */
export function useRemoteThreadComposerContext(input: {
  readonly threadRef: ScopedThreadRef;
  readonly runtimeId: string;
  readonly replica: CmmdRuntimeThreadReplica | null;
}): RemoteThreadComposerContext {
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const primaryServerConfig = useServerConfig();
  const settings = useSettings();
  const draft = useComposerThreadDraft(input.threadRef);
  const targetRuntime = useSavedEnvironmentRuntimeStore(
    (store) => store.byId[input.threadRef.environmentId],
  );
  const inventory = useCmmdRuntimeInventorySnapshot();
  const inventoryRuntime = inventory.inventory?.runtimes.find(
    (entry) => entry.runtime.id === input.runtimeId,
  );
  const { providerInventory, providerStatuses } = useDraftProviderInventory({
    targetEnvironmentId: input.threadRef.environmentId,
    runtimeId: input.runtimeId,
    primaryEnvironmentId,
    primaryServerConfig,
    targetEnvironmentServerConfig: targetRuntime?.serverConfig ?? null,
    runtimes: inventory.inventory?.runtimes ?? [],
    localDeviceRuntimeId: getStoredLocalDeviceRuntimeId(),
  });
  const credentialMode = remoteCredentialMode(inventoryRuntime, input.threadRef.environmentId);
  const access = useForgeProviderAccess({
    enabled: true,
    credentialMode,
    runtimeId: input.runtimeId,
  });
  const policy = providerPolicyForAccess(access);
  const inventoryThread = inventoryRuntime?.projects
    .flatMap((project) => project.threads)
    .find((thread) => thread.threadId === input.threadRef.threadId);
  const { selection, lockedProvider } = remoteProviderIdentity(input.replica, inventoryThread);
  const hasLiveInventory = providerInventory.pickerProviderProps.providers !== undefined;
  const plan = useMemo(
    () =>
      resolveComposerTurnPlan({
        thread: undefined,
        selectedProvider:
          draft.activeProvider ??
          selection?.provider ??
          settings.textGenerationModelSelection.provider,
        projectDefaultProvider: null,
        providers: providerStatuses,
        access,
        credentialMode,
        hasLiveInventory,
      }),
    [
      draft.activeProvider,
      selection?.provider,
      settings.textGenerationModelSelection.provider,
      providerStatuses,
      access,
      credentialMode,
      hasLiveInventory,
    ],
  );
  return {
    workspace: decodeCmmdRuntimeThreadWorkspaceMetadata(inventoryThread?.metadata),
    threadGoal: remoteThreadGoal(inventoryThread, input.replica),
    lockedProvider,
    providers: providerStatuses,
    availability: plan.pickRows,
    policy,
    modelSelection: selection,
    disabledReason: plan.refusalReason,
  };
}

export function remoteCredentialMode(
  runtime: CmmdRuntimeInventoryRuntime | undefined,
  environmentId: EnvironmentId,
): "cmmd-cloud" | "runtime" {
  return runtime?.runtime.runtimeKind === "cloud-vm" || classifyEnvironmentId(environmentId).isCloud
    ? "cmmd-cloud"
    : "runtime";
}

function remoteProviderIdentity(
  replica: CmmdRuntimeThreadReplica | null,
  inventory: CmmdRuntimeInventoryThreadSummary | undefined,
) {
  return resolveRemoteThreadProviderIdentity({
    replica: replica
      ? {
          provider: replica.thread.provider,
          model: replica.thread.model,
          providerAccountId: replica.thread.providerAccountId,
        }
      : null,
    inventory: inventory
      ? {
          provider: inventory.provider,
          model: inventory.model,
          providerAccountId: inventory.providerAccountId,
        }
      : null,
    hasUserMessage: replica?.events.some((event) => event.kind === "user_message") ?? false,
  });
}

export function remoteThreadGoal(
  inventory: CmmdRuntimeInventoryThreadSummary | undefined,
  replica: CmmdRuntimeThreadReplica | null = null,
) {
  const goal = decodeCmmdRuntimeThreadGoalMetadata(inventory?.metadata ?? replica?.thread.metadata);
  return goal ? { ...goal, taskManaged: inventory?.anchorKind === "task" } : null;
}
